import { useLayoutEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { DEFAULT_LIST_PAGE_SIZE } from '../constants/pagination';
import {
  buildListQuery,
  listQueryEqual,
  ListFilterSpec,
  parseListQuery,
} from '../utils/listQueryParams';

export type ListRestore<F> = {
  page?: number;
  perPage?: number;
  filters?: F;
};

export function useListUrlState<F>(options: {
  spec: ListFilterSpec<F>;
  defaultPerPage?: number;
  restore?: ListRestore<F> | null;
}) {
  const { spec, defaultPerPage = DEFAULT_LIST_PAGE_SIZE, restore } = options;
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const fromUrl = parseListQuery<F>(location.search, spec, defaultPerPage);

  const [filters, setFilters] = useState<F>(() =>
    restore?.filters ? { ...restore.filters } : fromUrl.filters
  );
  const [page, setPage] = useState(() =>
    typeof restore?.page === 'number' && restore.page >= 1 ? restore.page : fromUrl.page
  );
  const [perPage, setPerPage] = useState(() =>
    typeof restore?.perPage === 'number' && restore.perPage >= 1 ? restore.perPage : fromUrl.perPage
  );

  useLayoutEffect(() => {
    const current = new URLSearchParams(location.search.startsWith('?') ? location.search.slice(1) : location.search);
    const desired = buildListQuery(current, { page, perPage, filters }, spec, defaultPerPage);
    const restorePending = Boolean((location.state as { restore?: unknown } | null)?.restore);
    if (listQueryEqual(desired, current) && !restorePending) {
      return;
    }
    setSearchParams(desired, { replace: true, state: restorePending ? {} : location.state });
  }, [page, perPage, filters, spec, defaultPerPage, setSearchParams, location.search, location.state]);

  return { page, setPage, perPage, setPerPage, filters, setFilters };
}
